import React, { useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/ClerkAuthContext';
import { AlertCircle, CheckCircle2, XCircle, X } from 'lucide-react';

interface ErrorGroup {
  id: number;
  organization_id: string;
  application_id: string;
  fingerprint_primary: string;
  error_type: string;
  representative_message: string;
  representative_stack: string[];
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  status: 'active' | 'resolved' | 'ignored';
}

interface ErrorListMinimalProps {
  appId: string;
  onAskAgentToFix?: () => void;
  onAskAgentToFixError?: (error: ErrorGroup) => void;
  isExpanded?: boolean;
  selectedErrorId?: number | null;
  onErrorClick?: (errorId: number) => void;
  onClose?: () => void;
}

export const ErrorListMinimal: React.FC<ErrorListMinimalProps> = ({
  appId,
  onAskAgentToFix,
  onAskAgentToFixError,
  isExpanded = false,
  selectedErrorId = null,
  onErrorClick,
  onClose
}) => {
  const { getToken } = useAuth();
  const [errorGroups, setErrorGroups] = useState<ErrorGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sentToAgent, setSentToAgent] = useState(false);
  const isInitialLoad = React.useRef(true);

  // Reset sentToAgent when selected error changes
  useEffect(() => {
    setSentToAgent(false);
  }, [selectedErrorId]);

  // When expanded and selected error is not found (e.g., after refresh), select the first error
  useEffect(() => {
    if (isExpanded && selectedErrorId && errorGroups.length > 0) {
      const selectedExists = errorGroups.find(g => g.id === selectedErrorId);
      if (!selectedExists && onErrorClick) {
        onErrorClick(errorGroups[0].id);
      }
    }
  }, [errorGroups, isExpanded, selectedErrorId, onErrorClick]);

  useEffect(() => {
    fetchErrorGroups(true);
    // Refresh every 30 seconds
    const interval = setInterval(() => fetchErrorGroups(false), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const fetchErrorGroups = async (showLoading = true) => {
    try {
      // Only show loading spinner on initial load, not on polling refreshes
      if (showLoading) {
        setLoading(true);
      }
      const token = await getToken();
      if (!token) {
        setError('Authentication required');
        return;
      }
      console.log('[ErrorListMinimal] Fetching errors for appId:', appId);
      const response = await apiService.getErrorGroupsByApp(token, appId, 'active', undefined, 5);
      console.log('[ErrorListMinimal] Response:', response);
      setErrorGroups(response.groups || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch error groups:', err);
      setError('Failed to load errors');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const formatTimestamp = (timestamp: string) => {
    // Parse the timestamp - could be ISO string or Unix timestamp (seconds)
    let date: Date;
    const numericTimestamp = Number(timestamp);

    if (!isNaN(numericTimestamp)) {
      // If it's a number, check if it's in seconds (< year 3000) or milliseconds
      // Unix timestamps in seconds are typically 10 digits, milliseconds are 13
      if (numericTimestamp < 10000000000) {
        // Likely seconds, convert to milliseconds
        date = new Date(numericTimestamp * 1000);
      } else {
        // Already in milliseconds
        date = new Date(numericTimestamp);
      }
    } else {
      // Try parsing as ISO string
      date = new Date(timestamp);
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="px-3 py-2">
        <div className="animate-pulse space-y-2">
          <div className="h-3 bg-gray-200 rounded w-16"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center gap-1 text-xs text-red-600">
          <XCircle className="h-3 w-3" />
          <span>Error loading</span>
        </div>
      </div>
    );
  }

  const activeCount = errorGroups.length;
  const selectedError = selectedErrorId ? errorGroups.find(g => g.id === selectedErrorId) : null;

  return (
    <div className={`border-t transition-all duration-300 ${isExpanded ? 'h-full flex flex-col' : ''}`}>
      <div className={`px-3 py-2 transition-all duration-300 ${isExpanded ? 'flex-1 overflow-auto' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
            {activeCount > 0 ? (
              <>
                <AlertCircle className="h-3 w-3 text-red-500" />
                <span>Errors ({activeCount})</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                <span>No Errors</span>
              </>
            )}
          </h3>
          {isExpanded && onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              title="Close error details"
            >
              <X className="h-3 w-3 text-gray-600" />
            </button>
          )}
        </div>

        {activeCount === 0 ? (
          <div className="text-xs text-gray-500 py-2">
            All clear!
          </div>
        ) : isExpanded && selectedError ? (
          // Expanded view showing selected error details
          <div className="space-y-3">
            <div className="text-xs p-3 bg-red-50 border border-red-200 rounded">
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="font-mono text-gray-800 font-semibold">
                  {selectedError.error_type}
                </span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                  {selectedError.occurrence_count}
                </Badge>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-[10px] text-gray-500 mb-1">Message:</p>
                  <p className="text-gray-700">{selectedError.representative_message}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 mb-1">First seen:</p>
                  <p className="text-gray-600">{formatTimestamp(selectedError.first_seen_at)} ago</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 mb-1">Last seen:</p>
                  <p className="text-gray-600">{formatTimestamp(selectedError.last_seen_at)} ago</p>
                </div>
                {selectedError.representative_stack && selectedError.representative_stack.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-500 mb-1">Stack trace:</p>
                    <pre className="text-[10px] bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto">
                      {selectedError.representative_stack.join('\n')}
                    </pre>
                  </div>
                )}
              </div>
              {onAskAgentToFixError && (
                sentToAgent ? (
                  <div className="w-full mt-3 text-xs py-1.5 px-2 text-center text-gray-600">
                    Sent to agent
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      onAskAgentToFixError(selectedError);
                      setSentToAgent(true);
                    }}
                    className="w-full mt-3 text-xs py-1.5 px-2 border border-blue-600 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    Ask agent to fix this error
                  </button>
                )
              )}
            </div>
            <div className="text-xs">
              <p className="text-gray-500 mb-2">All errors ({activeCount}):</p>
              <div className="space-y-1">
                {errorGroups.map(group => (
                  <div
                    key={group.id}
                    className={`p-2 rounded cursor-pointer transition-colors ${
                      group.id === selectedErrorId
                        ? 'bg-red-100 border border-red-200'
                        : 'bg-red-50 border border-red-100 hover:bg-red-100'
                    }`}
                    onClick={() => onErrorClick?.(group.id)}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <span className="font-mono text-gray-700 truncate flex-1">
                        {group.error_type}: {group.representative_message}
                      </span>
                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                        {group.occurrence_count}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {errorGroups.slice(0, 3).map(group => (
              <div
                key={group.id}
                className="text-xs p-2 bg-red-50 border border-red-100 rounded cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => onErrorClick?.(group.id)}
              >
                <div className="flex items-start justify-between gap-1 mb-1">
                  <span className="font-mono text-gray-700 truncate flex-1" title={group.error_type}>
                    {group.error_type}
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                    {group.occurrence_count}
                  </Badge>
                </div>
                <p className="text-gray-600 line-clamp-1" title={group.representative_message}>
                  {group.representative_message}
                </p>
                <span className="text-[10px] text-gray-400">
                  {formatTimestamp(group.last_seen_at)} ago
                </span>
              </div>
            ))}
            {activeCount > 3 && (
              <div
                className="text-xs text-center py-1 text-blue-600 hover:text-blue-700 cursor-pointer"
                onClick={() => {
                  if (onErrorClick && errorGroups.length > 3) {
                    onErrorClick(errorGroups[3].id);
                  }
                }}
              >
                +{activeCount - 3} more
              </div>
            )}
            {onAskAgentToFix && (
              <button
                onClick={onAskAgentToFix}
                className="w-full mt-2 text-xs py-1.5 px-2 border border-blue-600 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              >
                Ask agent to fix
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
